import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

function ProtectedRoute({ children }) {
    const location = useLocation();
    const [shouldRedirect, setShouldRedirect] = useState(false);
    // const [accessToken, setAccessToken] = useState(localStorage.getItem("access_token"));
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const toastShown = useRef(false);
    const [accessToken, setAccessToken] = useState(() => {
        // 1. Check the URL for a token immediately during the first render
        const urlParams = new URLSearchParams(location.search);
        const tokenFromUrl = urlParams.get("token");

        if (tokenFromUrl) {
            // 2. If a new token is in the URL, save it and use it right away
            localStorage.setItem("access_token", tokenFromUrl);
            return tokenFromUrl;
        }
        // 3. Otherwise, fall back to the old token in local storage
        return localStorage.getItem("access_token");
    });

    // 🔹 Logout Helper
    const logoutUser = (message) => {
        if (!toastShown.current) {
            toast.error(message || "Session expired. Please log in again.");
            toastShown.current = true;
        }
        setTimeout(() => {
            localStorage.removeItem("access_token");
            setShouldRedirect(true);
        }, 2000);
    };

    // 🔹 Token Validation
    const validateToken = async () => {
        if (!accessToken) return;

        try {
            const res = await axios.get("https://api.dental360grp.com/validate_token", {
                headers: { Authorization: `Bearer ${accessToken}` },
                timeout: 7000,
            });

            if (!res.data?.success) {
                logoutUser("Session expired. Please log in again.");
            } else {
                setIsOffline(false);
            }
        } catch (err) {
            console.error("Token validation error:", err.message || err);

            // ⚠️ Handle network/timeout safely
            if (!err.response) {
                setIsOffline(true);
                return; // 🚫 Do not log out when offline
            }

            // 🔒 Real authentication errors
            const status = err.response?.status;
            if ([401, 403].includes(status)) {
                logoutUser("Authentication failed. Please log in again.");
            } else {
                toast.error("Unexpected error occurred. Please try again.");
            }
        }
    };

    // 🔹 Online/offline detection
    useEffect(() => {
        const handleOffline = () => {
            setIsOffline(true);
            toast.error("You've gone offline. Some features may not work.");
        };

        const handleOnline = () => {
            setIsOffline(false);
            toast.success("Connection restored.");
            validateToken();
        };

        window.addEventListener("offline", handleOffline);
        window.addEventListener("online", handleOnline);

        return () => {
            window.removeEventListener("offline", handleOffline);
            window.removeEventListener("online", handleOnline);
        };
    }, []);

    // 🔹 Validate on route change
    useEffect(() => {
        validateToken();
    }, [location.pathname, accessToken]);

    // 🔹 Background recheck every 5 minutes
    useEffect(() => {
        const interval = setInterval(validateToken, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [accessToken]);

    // 🔹 Handle token from URL
    // useEffect(() => {
    //     const urlParams = new URLSearchParams(location.search);
    //     const tokenFromUrl = urlParams.get("token");

    //     if (tokenFromUrl) {
    //         localStorage.setItem("access_token", tokenFromUrl);
    //         setAccessToken(tokenFromUrl);
    //     } else if (!accessToken && !toastShown.current) {
    //         toast.error("Unauthorized access. Please log in.");
    //         toastShown.current = true;
    //         setShouldRedirect(true);
    //     }
    // }, [location.search, accessToken]);

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const tokenFromUrl = urlParams.get("token");

        if (tokenFromUrl && tokenFromUrl !== accessToken) {
            localStorage.setItem("access_token", tokenFromUrl);
            setAccessToken(tokenFromUrl);
        }

        // Security improvement: Remove the token from the URL bar so users don't copy/share it
        if (tokenFromUrl) {
            urlParams.delete("token");
            const search = urlParams.toString();
            const newUrl = `${location.pathname}${search ? "?" + search : ""}${location.hash}`;
            window.history.replaceState({}, document.title, newUrl);
        }

        if (!accessToken && !tokenFromUrl && !toastShown.current) {
            toast.error("Unauthorized access. Please log in.");
            toastShown.current = true;
            setShouldRedirect(true);
        }
    }, [location.search, location.pathname, location.hash, accessToken]);

    // 🔹 Redirect if needed
    useEffect(() => {
        if (shouldRedirect) {
            window.location.href = "/auth/sign-in";
        }
    }, [shouldRedirect]);

    // 🔹 Render (Tailwind UI equivalent with React.createElement)
    return accessToken
        ? React.createElement(
            "div",
            null,
            isOffline &&
            React.createElement(
                "div",
                {
                    className:
                        "fixed top-0 left-0 w-full text-center py-3 px-4 z-[9999] " +
                        "backdrop-blur-md bg-gradient-to-r from-amber-400/90 to-yellow-500/90 " +
                        "text-white font-medium shadow-lg animate-slideDown",
                },
                "You're offline — some features may be temporarily unavailable."
            ),
            children
        )
        : null;
}

export default ProtectedRoute;
